import { useState, useRef, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, CircleCheck } from 'lucide-react';

import { COACH_ACTIVITY_STATUS } from '@/app/coachActivity/enums';

import Loader from '@/components/common/Loader';
import Error from '@/components/common/Error';
import SpinnerButton from '@/components/common/SpinnerButton';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

import { useApplication } from '@/data/application';
import {
  useCoachActivity,
  useSubmitApplication,
  useCoachActivities
} from '@/data/coachActivity';

const ApplicationStepsContinue = ({ steps }) => {
  const params = useParams();
  const coachActivity = useCoachActivity(params.coachActivityId);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const stepRefs = useRef([]);
  const [lineHeights, setLineHeights] = useState([]);
  const observerRef = useRef(null);
  const submitApplication = useSubmitApplication();
  const coachActivities = useCoachActivities();

  const isApplicationComplete = steps.every((step) => step.isComplete);

  useEffect(() => {
    // Initialize the ResizeObserver
    observerRef.current = new ResizeObserver(() => {
      const heights = [...steps].map((_, index) => {
        if (!stepRefs.current[index] || !stepRefs.current[index + 1]) return 0;
        return (
          stepRefs.current[index + 1].offsetTop -
          stepRefs.current[index].offsetTop -
          16
        );
      });
      setLineHeights(heights);
    });

    // Observe each step element
    stepRefs.current.forEach((ref) => {
      if (ref) {
        observerRef.current.observe(ref);
      }
    });

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [steps]);

  const handleSubmitApplication = async () => {
    try {
      setSubmitting(true);
      const updatedCoachActivity = await submitApplication(
        coachActivity.data.id
      );
      coachActivity.mutate(updatedCoachActivity, false);
      coachActivities.mutate();
    } catch (error) {
      setApiError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {apiError && <Error error={apiError} />}
      {coachActivity.data.status === COACH_ACTIVITY_STATUS.SUBMITTED && (
        <Alert variant="success">
          <CircleCheck size={18} />
          <AlertTitle>Submitted</AlertTitle>
          <AlertDescription>
            Congratulations! Your application has been submitted. ProFound will
            review your application and let you know when it is approved.
          </AlertDescription>
        </Alert>
      )}
      <div className="py-6">
        <ul className="space-y-4">
          {steps.map((step, index) => (
            <li
              key={index}
              className="relative"
              ref={(el) => {
                stepRefs.current[index] = el;
                if (el && observerRef.current) {
                  observerRef.current.observe(el);
                }
              }}
            >
              <Link
                to={step.link}
                className="flex items-start p-2 -m-2 rounded-lg active:bg-muted transition-colors"
              >
                <div className="relative">
                  <div
                    className={`w-8 h-8 ${
                      step.isComplete ? 'bg-green-500' : 'bg-primary'
                    } rounded-full flex items-center justify-center flex-shrink-0`}
                  >
                    {step.isComplete ? (
                      <Check size={16} />
                    ) : (
                      <span className="text-white font-bold">{step.step}</span>
                    )}
                  </div>
                  {index < steps.length && (
                    <div
                      className="absolute left-1/2 -translate-x-1/2 w-[1px] bg-muted-foreground"
                      style={{
                        top: '2rem',
                        height: `${lineHeights[index] || 0}px`
                      }}
                    />
                  )}
                </div>
                <div className="ml-4 flex-grow">
                  <h3 className="font-semibold text-lg">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">
                    {step.subTitle}
                  </p>
                </div>
                <svg
                  className="w-6 h-6 text-muted-foreground flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </li>
          ))}
          <li
            className="relative"
            ref={(el) => {
              stepRefs.current[steps.length] = el;
              if (el && observerRef.current) {
                observerRef.current.observe(el);
              }
            }}
          >
            <Link
              to="#"
              className="flex items-start p-2 -m-2 rounded-lg active:bg-muted transition-colors"
            >
              <div className="relative">
                <div
                  className={`w-8 h-8 ${
                    [
                      COACH_ACTIVITY_STATUS.SUBMITTED,
                      COACH_ACTIVITY_STATUS.APPROVED
                    ].includes(coachActivity.data.status)
                      ? 'bg-green-500'
                      : 'bg-primary'
                  } rounded-full flex items-center justify-center flex-shrink-0`}
                >
                  {[
                    COACH_ACTIVITY_STATUS.SUBMITTED,
                    COACH_ACTIVITY_STATUS.APPROVED
                  ].includes(coachActivity.data.status) ? (
                    <Check size={16} />
                  ) : (
                    <span className="text-white font-bold">
                      {steps.length + 1}
                    </span>
                  )}
                </div>
              </div>
              <div className="ml-4 flex-grow">
                <h3 className="font-semibold text-lg">Submit Application</h3>
                <p className="text-muted-foreground text-sm">
                  Review and submit your completed application
                </p>
                <div className="flex justify-end py-4">
                  {[
                    COACH_ACTIVITY_STATUS.PENDING,
                    COACH_ACTIVITY_STATUS.REJECTED
                  ].includes(coachActivity.data.status) && (
                    <SpinnerButton
                      disabled={!isApplicationComplete || submitting}
                      onClick={handleSubmitApplication}
                      loading={submitting}
                    >
                      {coachActivity.data.status ===
                      COACH_ACTIVITY_STATUS.REJECTED
                        ? 'Resubmit'
                        : 'Submit'}
                    </SpinnerButton>
                  )}
                </div>
              </div>
            </Link>
          </li>
        </ul>
      </div>
    </>
  );
};

const ApplicationStepsContinueLoader = ({ coachActivityId }) => {
  const applicationStructure = useApplication({ coachActivityId });

  if (applicationStructure.isLoading && !applicationStructure.data) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <Loader />
      </div>
    );
  }

  return <ApplicationStepsContinue steps={applicationStructure.data.steps} />;
};

export default ApplicationStepsContinueLoader;
